import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useEffect, useState } from 'react';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import {
	H4,
	Caption,
	Select,
	DateRangePicker,
	DEFAULT_PRESETS,
} from '@components';
import { useGetDifotShipperOptions, useGetLocationGroups, useGetShipperAnalyticsLocations } from '@api';
import { TAnalyticsLocationOption, TDateRange, TDateRangePreset, TEXT_WEIGHT } from '@types';
import { formatDate } from '@utils';
import styles from './AnalyticsFilterBar.module.scss';

const LAST_30_DAYS: TDateRangePreset = {
	key: 'last_30_days',
	label: 'Last 30 days',
	resolve: (t) => ({ start: subDays(t, 29), end: t }),
}

const LAST_90_DAYS: TDateRangePreset = {
	key: 'last_90_days',
	label: 'Last 90 days',
	resolve: (t) => ({ start: subDays(t, 89), end: t }),
}

const LAST_12_MONTHS: TDateRangePreset = {
	key: 'last_12_months',
	label: 'Last 12 months',
	resolve: (t) => ({ start: subMonths(t, 12), end: t }),
}

const ANALYTICS_PRESETS: TDateRangePreset[] = [
	...DEFAULT_PRESETS,
	LAST_30_DAYS,
	LAST_90_DAYS,
	LAST_12_MONTHS,
];

export interface IAnalyticsFilterBarProps {
	shippers: number[];
	onShippersChange: (ids: number[]) => void;
	locationGroups: number[];
	onLocationGroupsChange: (ids: number[]) => void;
	pickupLocations: string[];
	onPickupLocationsChange: (ids: string[]) => void;
	dropoffLocations: string[];
	onDropoffLocationsChange: (ids: string[]) => void;
	dateRange: TDateRange;
	onDateRangeChange: (range: TDateRange) => void;
}

type TOption = {
	label: string;
	value: number;
}

/**
 * The filter band that heads every Analytics dashboard tab.
 */
export const AnalyticsFilterBar = (props: IAnalyticsFilterBarProps) => {
	const {
		shippers,
		onShippersChange,
		locationGroups,
		onLocationGroupsChange,
		pickupLocations,
		onPickupLocationsChange,
		dropoffLocations,
		onDropoffLocationsChange,
		dateRange,
		onDateRangeChange,
	} = props;

	const [shipperOptions, setShipperOptions] = useState<TOption[]>([]);
	const [groupOptions, setGroupOptions] = useState<TOption[]>([]);
	const [pickupOptions, setPickupOptions] = useState<TAnalyticsLocationOption[] | null>(null);
	const [dropoffOptions, setDropoffOptions] = useState<TAnalyticsLocationOption[] | null>(null);
	const { callGetDifotShipperOpts } = useGetDifotShipperOptions();
	const { callGetLocationGroups } = useGetLocationGroups();
	const { callGetShipperAnalyticsLocations } = useGetShipperAnalyticsLocations();
	const shippersParam = shippers.length > 0 ? shippers.join(',') : '';

	const keepAvailable = (selected: string[], options: TAnalyticsLocationOption[]) =>
		selected.filter((id) => options.some((option) => option.id === id));

	useEffect(() => {
		callGetDifotShipperOpts().then((res) => {
			if (res?.data?.data) setShipperOptions(res.data.data);
		}).catch((err) => {
			console.error('Failed to load DIFOT shipper options', err);
		});
	}, []);

	useEffect(() => {
		callGetLocationGroups().then((res) => {
			if (res?.data) setGroupOptions(res.data.map((g) => ({ label: g.name, value: g.id })));
		}).catch((err) => {
			console.error('Failed to load location groups', err);
		});
	}, []);

	useEffect(() => {
		callGetShipperAnalyticsLocations({
			params: {
				start: formatDate(dateRange.start, 'API_DATE_FORMAT'),
				end: formatDate(dateRange.end, 'API_DATE_FORMAT'),
				...(shippersParam ? { shipper_ids: shippersParam } : {}),
			}
		}).then((res) => {
			if (!res?.data) return;
			setPickupOptions(res.data.pickup);
			setDropoffOptions(res.data.dropoff);
		}).catch((err) => {
			console.error('Failed to load analytics locations', err);
		});
	}, [dateRange.start, dateRange.end, shippersParam]);

	/* Options follow the date range and shippers, and only count delivered
	   freight, so a selected location can drop out of the list. A selection the
	   dropdown can't show would silently filter every chart, so it's dropped.
	   null means not loaded yet, which must not clear anything. */
	useEffect(() => {
		if (pickupOptions === null) return;
		const available = keepAvailable(pickupLocations, pickupOptions);
		if (available.length !== pickupLocations.length) onPickupLocationsChange(available);
	}, [pickupOptions]);

	useEffect(() => {
		if (dropoffOptions === null) return;
		const available = keepAvailable(dropoffLocations, dropoffOptions);
		if (available.length !== dropoffLocations.length) onDropoffLocationsChange(available);
	}, [dropoffOptions]);

	return (
		<div className={styles.filtersBar} data-testid={'@Ofload-AnalyticsFilterBar-Container'}>
			<div className={styles.filtersLabel}>
				<H4 weight={TEXT_WEIGHT.SEMI_BOLD}>Filters</H4>
			</div>
			<div className={styles.filterControls}>
				<div className={styles.filterField}>
					<Caption weight={TEXT_WEIGHT.SEMI_BOLD} color={'var(--neutral-06)'}>Date range</Caption>
					<DateRangePicker value={dateRange} onChange={onDateRangeChange} presets={ANALYTICS_PRESETS} displayThin={true} />
				</div>
				<div className={styles.filterField}>
					<Caption weight={TEXT_WEIGHT.SEMI_BOLD} color={'var(--neutral-06)'}>Shipper</Caption>
					<Select
						name={'shipper_filter'}
						multiple
						displayThin={true}
						width={'210px'}
						placeholder={'All shippers'}
						value={shippers}
						onChange={(ids: number[]) => onShippersChange(ids ?? [])}
					>
						{shipperOptions.map((opt) => (
							<Select.Option key={opt.value} value={opt.value}>
								{opt.label}
							</Select.Option>
						))}
					</Select>
				</div>
				<div className={styles.filterField}>
					<Caption weight={TEXT_WEIGHT.SEMI_BOLD} color={'var(--neutral-06)'}>Location group</Caption>
					<Select
						name={'location_group_filter'}
						multiple
						displayThin={true}
						width={'210px'}
						placeholder={'All location groups'}
						value={locationGroups}
						onChange={(ids: number[]) => onLocationGroupsChange(ids ?? [])}
					>
						{groupOptions.map((opt) => (
							<Select.Option key={opt.value} value={opt.value}>
								{opt.label}
							</Select.Option>
						))}
					</Select>
				</div>
				<div className={styles.filterField}>
					<div className={styles.locationPairLabels}>
						<Caption weight={TEXT_WEIGHT.SEMI_BOLD} color={'var(--neutral-06)'}>Pick-up</Caption>
						<Caption weight={TEXT_WEIGHT.SEMI_BOLD} color={'var(--neutral-06)'}>Drop-off</Caption>
					</div>
					<div className={styles.locationPair}>
						<Select
							multiple={true}
							borderless={true}
							name={'pickup_locations'}
							value={pickupLocations}
							onChange={(ids: string[]) => onPickupLocationsChange(ids ?? [])}
							displayThin={true}
							keepErrorSpace={false}
							placeholder={'All locations'}
							width={'210px'}
						>
							{(pickupOptions ?? []).map((location) => (
								<Select.Option value={location.id} key={location.id}>{location.name}</Select.Option>
							))}
						</Select>
						<ArrowForwardIcon className={styles.locationPairArrow} fontSize={'small'} />
						<Select
							multiple={true}
							borderless={true}
							name={'dropoff_locations'}
							value={dropoffLocations}
							onChange={(ids: string[]) => onDropoffLocationsChange(ids ?? [])}
							displayThin={true}
							keepErrorSpace={false}
							placeholder={'All locations'}
							width={'210px'}
						>
							{(dropoffOptions ?? []).map((location) => (
								<Select.Option value={location.id} key={location.id}>{location.name}</Select.Option>
							))}
						</Select>
					</div>
				</div>
			</div>
		</div>
	);
}
